import SideDrawer from "app/components/SideDrawer/SideDrawer";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  getSampleDataInfo,
  selectSampleDataInfo,
  selectGetSampleDataInfoStatus,
  openCreateTeamModal,
  openEditDepartmentModal,
} from "app/components/Modals/slice";
import {
  selectConfigCatFlag,
  selectCurrentUserAccountId,
  selectCurrentUserInfo,
  selectIsCurrentUserAdmin,
} from "app/containers/Global/slice";
import {
  selectAiFeaturesEnabled,
  selectIsDepartmentsHidden,
  selectIsInviteModalOpen,
  selectTeamAndDepartmentLeadIdsForLoggedInUser,
  setInviteModalOpen,
} from "app/containers/AdminConsole/slice";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  selectUserIsAbleToCreateTeams,
  selectUserIsAbleToInviteUsers,
} from "app/containers/UserGuide/slice";
import {
  selectIsQuickActionDrawerOpen,
  setIsQuickActionDrawerOpen,
} from "./slice";
import { useIsMobile } from "utils/hooks";
import SampleWeeklyCheckInEmailReportsModal from "app/components/WeeklyCheckInComponents/SampleComponents/SampleWeeklyCheckInEmailReportsModal";
import {
  selectSampleWeeklyCheckInEmailReportModalState,
  setSampleWeeklyCheckInEmailReportModalState,
} from "app/containers/WeeklyCheckInMonitor/slice";
import { WEEKLY_CHECK_IN_FEATURE_NAME } from "app/containers/WeeklyCheckInMonitor/constants";
import { CONFIG_CAT_FLAGS } from "../GlobalNavbar/constants";
import { onSampleWeeklyCheckInClick } from "./helpers";
import { Collapse, Nav } from "react-bootstrap";
import { selectManagerAndDirectReportsByUserAccountId } from "app/containers/AdvancedCsvUpload/slice";
import { MENU_STRUCTURE_NEW } from "./constants";
import { MenuItem } from "./types";
import { faArrowUpRight } from "@fortawesome/pro-regular-svg-icons";
import { showScheduleAssessmentModal } from "../LaunchAssessmentModal/slice";
import InviteUsersModal from "../Modals/InviteUsersModal";

export default function QuickActionDrawer() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const showing = useAppSelector(selectIsQuickActionDrawerOpen);
  const sampleDataInfo = useAppSelector(selectSampleDataInfo);
  const usersInfo = useAppSelector(selectCurrentUserInfo);
  const getSampleDataInfoStatus = useAppSelector(selectGetSampleDataInfoStatus);
  const areDepartmentsHidden = useAppSelector(selectIsDepartmentsHidden);
  const canUserInviteNewMembers = useAppSelector(selectUserIsAbleToInviteUsers);
  const canUserCreateTeams = useAppSelector(selectUserIsAbleToCreateTeams);
  const currentUserAccountId = useAppSelector(selectCurrentUserAccountId);
  const isAdmin = useAppSelector(selectIsCurrentUserAdmin);
  const teamAndDepartmentLeaderOf = useAppSelector(
    selectTeamAndDepartmentLeadIdsForLoggedInUser
  );
  const areAIFeaturesEnabled = useAppSelector(selectAiFeaturesEnabled);
  const weeklyCheckInConfigCatFlag = useAppSelector(
    selectConfigCatFlag(CONFIG_CAT_FLAGS.weeklyCheckIn.savedFlagName)
  );
  const sampleWeeklyCheckInEmailReportModalState = useAppSelector(
    selectSampleWeeklyCheckInEmailReportModalState
  );
  const managersAndDirectReportsByUserAccountId = useAppSelector(
    selectManagerAndDirectReportsByUserAccountId
  );
  const isInviteModalOpen = useAppSelector(selectIsInviteModalOpen);

  const [showAllSampleData, setShowAllSampleData] = useState(true);
  const [slightlyShowing, setSlightlyShowing] = useState(false);
  const [collapseMapState, setCollapseMapState] = useState<
    Record<string, boolean>
  >({
    [`${WEEKLY_CHECK_IN_FEATURE_NAME}s`]: true,
    "Setup Organization": true,
    "Check-Ins": true,
    "Launch TEAMscan": true,
  });

  const showWeeklyCheckInData =
    weeklyCheckInConfigCatFlag && areAIFeaturesEnabled;

  const isUserManager = useMemo(
    () =>
      !!(
        currentUserAccountId &&
        managersAndDirectReportsByUserAccountId?.[currentUserAccountId]
          ?.directReportRelationships?.length
      ),
    [managersAndDirectReportsByUserAccountId, currentUserAccountId]
  );

  const isUserTeamLeader = useMemo(
    () => !!teamAndDepartmentLeaderOf?.teams?.length,
    [teamAndDepartmentLeaderOf]
  );

  // The sample data ids are only used by the sample report links, so fetch them once the drawer is opened
  useEffect(() => {
    if (showing && getSampleDataInfoStatus === "idle") {
      dispatch(getSampleDataInfo());
    }
  }, [dispatch, showing, getSampleDataInfoStatus]);

  // Do not delete this yet we will need this for the new quick actions
  const navigateWrapperFn = (path: string, state?: any) => {
    navigate(path, state);
    if (isMobile) {
      setShowing(false);
    }
  };

  const getClassName = () => {
    if (showing) {
      return "quick-actions open";
    }
    if (slightlyShowing) {
      return "quick-actions slightly-open";
    }

    return "quick-actions";
  };

  const getQuickActionsButtonClass = () => {
    if (showing) {
      return "quick-action-button open";
    }
    if (slightlyShowing) {
      return "quick-action-button slightly-open";
    }
    return "quick-action-button";
  };

  const getOnLaunchWeeklyCheckInNavigationPath = (): string | null => {
    if (!showWeeklyCheckInData) {
      return null;
    }

    if (isAdmin) {
      return "/AdminConsole/Organization%20Settings/Check-Ins";
    }

    if (!!teamAndDepartmentLeaderOf?.departments?.length) {
      const firstDepartmentId = teamAndDepartmentLeaderOf.departments[0];
      return `/DepartmentGuide/${firstDepartmentId}?tab=Department+Settings`;
    }

    if (isUserTeamLeader) {
      const firstTeamId = teamAndDepartmentLeaderOf?.teams[0];
      return `/TeamGuide/${firstTeamId}/TeamSettings?teamSettingsActiveTab=general`;
    }

    if (isUserManager) {
      return "/UserSettings?userSettingsActiveTab=emailAndNotifications";
    }

    return null;
  };

  const setShowing = (showing: boolean) => {
    dispatch(setIsQuickActionDrawerOpen(showing));
  };

  const getSetUpOrganizationSection = () => {
    if (
      !canUserInviteNewMembers &&
      !canUserCreateTeams &&
      !isAdmin &&
      !areDepartmentsHidden
    ) {
      return null;
    }

    return getCollapsibleSection(
      "Setup Organization",
      <>
        {canUserInviteNewMembers &&
          getSectionCard(
            MENU_STRUCTURE_NEW.quickActions.setupOrganization.items[
              "Add People to Organization"
            ],
            () => {
              if (isAdmin) {
                navigateWrapperFn(`/AdminConsole/People`, {
                  state: {
                    openInviteModal: true,
                  },
                });
                return;
              }
              dispatch(setInviteModalOpen({ isOpen: true }));
            }
          )}
        {canUserCreateTeams &&
          getSectionCard(
            MENU_STRUCTURE_NEW.quickActions.setupOrganization.items[
              "Create a new team"
            ],
            () => {
              if (isAdmin) {
                navigateWrapperFn(`/AdminConsole/Teams`);
              }
              dispatch(openCreateTeamModal());
            }
          )}
        {isAdmin &&
          !areDepartmentsHidden &&
          getSectionCard(
            MENU_STRUCTURE_NEW.quickActions.setupOrganization.items[
              "Set Up Departments"
            ],
            () => {
              navigateWrapperFn(`/AdminConsole?tab=Departments`);
              dispatch(openEditDepartmentModal());
            }
          )}
      </>
    );
  };

  const getNewBody = () => {
    if (showAllSampleData) {
      return (
        <div>
          {getSectionCard(
            MENU_STRUCTURE_NEW.sampleReports.checkIns.items["Check-Ins"],
            () => {
              onSampleWeeklyCheckInClick(usersInfo?.firstName ?? "", dispatch);
              dispatch(
                setSampleWeeklyCheckInEmailReportModalState({
                  isOpen: true,
                  type: "sample-weekly-check-in-chat",
                })
              );
            }
          )}
          {getCollapsibleSection(
            "TeamScan",
            <>
              {getSectionCard(
                MENU_STRUCTURE_NEW.sampleReports.teamScan.items[
                  "TEAMscan Survey"
                ],
                () => {
                  setShowing(false);
                  navigateWrapperFn("/survey/team360?eventId=sample");
                }
              )}
              {getSectionCard(
                MENU_STRUCTURE_NEW.sampleReports.teamScan.items[
                  "Team-level Report"
                ],
                () =>
                  navigateWrapperFn(
                    `TeamGuide/${sampleDataInfo?.teamScanTeamId}?tab=TEAMscan`
                  ),
                !sampleDataInfo
              )}
              {areDepartmentsHidden
                ? null
                : getSectionCard(
                    MENU_STRUCTURE_NEW.sampleReports.teamScan.items[
                      "Department-level Report"
                    ],
                    () =>
                      navigateWrapperFn(
                        `/DepartmentGuide/${sampleDataInfo?.teamScanDepartmentId}?tab=TEAMscan`
                      ),
                    !sampleDataInfo
                  )}
              {getSectionCard(
                MENU_STRUCTURE_NEW.sampleReports.teamScan.items[
                  "Organization Report"
                ],
                () => navigateWrapperFn("AdminConsole/Analytics?isSample=true")
              )}
            </>,
            "Sample TeamScan Reports"
          )}
          {getCollapsibleSection(
            "Personality Insights",
            <>
              {getSectionCard(
                MENU_STRUCTURE_NEW.sampleReports.personalityInsights.items[
                  "Individual Personality Report"
                ],
                () =>
                  navigateWrapperFn(
                    `UserGuide/${sampleDataInfo?.sampleUserAccountId}?tab=Workplace+Insights`
                  ),
                !sampleDataInfo
              )}
              {getSectionCard(
                MENU_STRUCTURE_NEW.sampleReports.personalityInsights.items[
                  "Team Personality Report"
                ],
                () =>
                  navigateWrapperFn(
                    `TeamGuide/${sampleDataInfo?.teamPersonalityTeamId}?tab=Team+Personality`
                  ),
                !sampleDataInfo
              )}
            </>,
            "Sample Personality Insights Reports"
          )}
          {getCollapsibleSection(
            "Profiles",
            <>
              {getSectionCard(
                MENU_STRUCTURE_NEW.sampleReports.profiles.items["User Profile"],
                () =>
                  navigateWrapperFn(
                    `/UserGuide/${sampleDataInfo?.sampleUserAccountId}?tab=About`
                  ),
                !sampleDataInfo
              )}
              {getSectionCard(
                MENU_STRUCTURE_NEW.sampleReports.profiles.items["Team Profile"],
                () =>
                  navigateWrapperFn(
                    `TeamGuide/${sampleDataInfo?.teamPersonalityTeamId}?tab=About`
                  ),
                !sampleDataInfo
              )}
            </>,
            "Sample Profiles"
          )}
        </div>
      );
    }

    const weeklyCheckInNavigationPath =
      getOnLaunchWeeklyCheckInNavigationPath();

    return (
      <div>
        {getSetUpOrganizationSection()}
        {weeklyCheckInNavigationPath &&
          getCollapsibleSection(
            "Check-Ins",
            <>
              {getSectionCard(
                MENU_STRUCTURE_NEW.quickActions.checkIns.items[
                  "Launch Check-Ins"
                ],
                () => navigateWrapperFn(weeklyCheckInNavigationPath)
              )}
            </>
          )}
        {getCollapsibleSection(
          "Launch TEAMscan",
          <>
            {getSectionCard(
              MENU_STRUCTURE_NEW.quickActions.launchTEAMscan.items[
                "Launch TEAMscan"
              ],
              () => dispatch(showScheduleAssessmentModal())
            )}
          </>
        )}
      </div>
    );
  };

  const toggleCollapse = (key: string) => {
    setCollapseMapState((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const getCollapsibleSection = (
    title: string,
    children: JSX.Element,
    key?: string
  ) => {
    const keyToUse = key ?? title;
    const collapseToggle = !!collapseMapState[keyToUse];
    return (
      <div className="quick-launch-drawer-collapsible-section">
        <button
          onClick={() => toggleCollapse(keyToUse)}
          className="no-style-button quick-launch-drawer-collapsible-section__title-container"
        >
          <FontAwesomeIcon icon={`chevron-${collapseToggle ? "up" : "down"}`} />
          <h3>{title}</h3>
        </button>
        <Collapse in={collapseMapState[keyToUse]}>
          <div className="quick-launch-drawer-collapsible-section__sub-items">
            {children}
          </div>
        </Collapse>
      </div>
    );
  };

  // disabled is used by the sample report cards until the sample data ids have loaded
  const getSectionCard = (
    menuItem?: MenuItem,
    onClick?: () => void,
    disabled?: boolean
  ) => {
    if (!menuItem) {
      return null;
    }
    return (
      <button
        className="no-style-button"
        onClick={onClick}
        disabled={disabled}
        style={disabled ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
      >
        <div className="quick-launch-drawer-sub-section">
          <div className="row-gap-8px align-items-center">
            {menuItem?.isNew && <span className="new-tag">New</span>}
            <span className="quick-launch-drawer-sub-section__title">
              {menuItem.title}
            </span>
            <FontAwesomeIcon
              icon={faArrowUpRight}
              className="quick-launch-drawer-sub-section__nav-icon"
            />
          </div>
          <p className="quick-launch-drawer-sub-section__description">
            {menuItem.description}
          </p>
        </div>
      </button>
    );
  };

  const isQuickActionsDisabled = () =>
    !canUserCreateTeams &&
    !canUserInviteNewMembers &&
    !isAdmin &&
    !isUserTeamLeader &&
    !isUserManager &&
    !showWeeklyCheckInData;

  const getNavigation = () => (
    <Nav
      className="simple-nav quick-actions__nav"
      activeKey={showAllSampleData ? "sample" : "quick-actions"}
    >
      <Nav.Item onClick={() => setShowAllSampleData(true)} key="sample">
        <Nav.Link eventKey="sample">Sample Reports</Nav.Link>
      </Nav.Item>
      <Nav.Item onClick={() => setShowAllSampleData(false)}>
        <Nav.Link eventKey="quick-actions" disabled={isQuickActionsDisabled()}>
          Quick Actions
        </Nav.Link>
      </Nav.Item>
    </Nav>
  );

  return (
    <>
      <SampleWeeklyCheckInEmailReportsModal
        type={sampleWeeklyCheckInEmailReportModalState.type}
        isOpen={sampleWeeklyCheckInEmailReportModalState.isOpen}
        onModalClose={() =>
          dispatch(
            setSampleWeeklyCheckInEmailReportModalState({ isOpen: false })
          )
        }
        onTypeChange={(type) =>
          dispatch(setSampleWeeklyCheckInEmailReportModalState({ type }))
        }
      />
      <InviteUsersModal
        showing={!!isInviteModalOpen}
        hideModal={() => {
          dispatch(setInviteModalOpen(false));
        }}
        teamId={isInviteModalOpen?.teamId}
        onInviteSuccess={() => dispatch(setInviteModalOpen(false))}
        defaultTab={isInviteModalOpen?.defaultTab}
      />
      <button
        className={getQuickActionsButtonClass()}
        onClick={() => setShowing(!showing)}
        onMouseEnter={() => {
          if (!showing) setSlightlyShowing(true);
        }}
        onMouseLeave={() => {
          if (!showing) setSlightlyShowing(false);
        }}
      >
        Quick Actions
        <FontAwesomeIcon
          icon={`circle-arrow-${showing ? "down" : "up"}`}
          className="ms-2"
        />
      </button>
      <SideDrawer
        show={true}
        onHide={() => {
          setShowing(false);
        }}
        title={"Quick Launch"}
        className={getClassName()}
        onBack={() => setShowAllSampleData(false)}
      >
        <>
          {getNavigation()}
          {getNewBody()}
        </>
      </SideDrawer>
    </>
  );
}
